import { useState, useEffect } from "react";
import { Menu, X, ExternalLink } from "lucide-react";

const links = [
  { name: "Home", href: "#hero" },
  { name: "Stats", href: "#stats" },
  { name: "Utilities", href: "#utilities" },
  { name: "Roadmap", href: "#roadmap" },
  { name: "Community", href: "#community" },
  { name: "Partners", href: "#partners" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      
      // Check if at top (hero section)
      if (window.scrollY < 100) {
        setActiveSection("#hero");
        return;
      }
      
      // Update active section based on scroll position
      // Check changelog first (it's part of roadmap), then other sections
      const changelogElement = document.getElementById("changelog");
      if (changelogElement) {
        const changelogRect = changelogElement.getBoundingClientRect();
        if (changelogRect.top <= 100 && changelogRect.bottom >= 100) {
          // Changelog is visible, highlight roadmap
          setActiveSection("#roadmap");
          return;
        }
      }
      
      // Check other sections (excluding hero, check in reverse order for better detection)
      const sections = links
        .filter(link => link.href !== "#hero")
        .map(link => link.href.substring(1))
        .reverse(); // Check from bottom to top
      
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      setActiveSection(current ? `#${current}` : "");
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Check on mount
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSmoothScroll = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
    setOpen(false);
  };

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-300 transform-gpu bg-black/40 backdrop-blur-xl border-b border-white/10 ${
        scrolled
          ? "backdrop-blur-2xl bg-black/60 border-white/20 shadow-lg shadow-purple-500/10"
          : ""
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <button
            type="button"
            className="flex items-center gap-2.5 sm:gap-3 group transition-transform duration-200 transform-gpu hover:scale-[1.02] active:scale-[0.98]"
            onClick={() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            <div className="transition-transform duration-300 transform-gpu group-hover:rotate-[-5deg]">
              <img 
                src="/project/logo192.png" 
                alt="RYS Logo" 
                className="w-9 h-9 sm:w-10 sm:h-10 transition-all duration-300 transform-gpu group-hover:drop-shadow-[0_0_15px_rgba(168,85,247,0.6)]"
                loading="eager"
                fetchPriority="high"
              />
            </div>
            <div className="flex flex-col">
              <span className="text-lg sm:text-xl font-bold gradient-text-static tracking-wide leading-tight">
                RYS
              </span>
              <span className="text-[9px] sm:text-[10px] text-gray-400 tracking-wide">
                RefundYourSOL
              </span>
            </div>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {links.map((link) => {
              const isActive = activeSection === link.href;
              return (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => handleSmoothScroll(e, link.href)}
                  className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 transform-gpu hover:-translate-y-0.5 active:translate-y-0 ${
                    isActive
                      ? "text-purple-400"
                      : "text-gray-300 hover:text-white"
                  }`}
                >
                  {link.name}
                  <span
                    className={`absolute inset-0 bg-purple-500/20 rounded-lg -z-10 transition-opacity duration-300 ${
                      isActive ? "opacity-100" : "opacity-0"
                    }`}
                  />
                  <span
                    className={`absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-purple-500 to-pink-500 transition-transform duration-300 origin-left transform-gpu ${
                      isActive ? "scale-x-100" : "scale-x-0"
                    }`}
                  />
                </a>
              );
            })}
          </nav>

          {/* Action Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="https://refundyoursol.com"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative px-5 py-2.5 rounded-xl bg-white/5 backdrop-blur-sm text-white text-sm font-medium border border-white/10 hover:border-white/20 transition-all duration-300 overflow-hidden transform-gpu hover:scale-105 active:scale-95"
            >
              <span className="relative z-10 flex items-center gap-2">
                Launch App
                <ExternalLink size={14} className="opacity-60 group-hover:opacity-100 transition-opacity" />
              </span>
              <span className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-pink-500/10 -translate-x-full group-hover:translate-x-0 transition-transform duration-300 transform-gpu" />
            </a>
            <a
              href="https://swap.pump.fun/?output=8gHPxqgHj6JQ2sQtMSghQYVN5qRP8wm5T6HNejuwpump"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative px-5 py-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 transition-all duration-300 overflow-hidden transform-gpu hover:scale-105 hover:-translate-y-0.5 active:scale-95"
            >
              <span className="relative z-10">Buy $RYS</span>
              <span className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </a>
          </div>

          {/* Mobile Toggle */}
          <button
            className="lg:hidden relative p-2 text-gray-200 hover:text-white transition-all duration-200 rounded-lg hover:bg-white/10 transform-gpu active:scale-90"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
          >
            <span className={`block transition-all duration-200 transform-gpu ${open ? "rotate-0 opacity-100" : "rotate-90 opacity-0 absolute inset-2"}`}>
              <X size={24} />
            </span>
            <span className={`block transition-all duration-200 transform-gpu ${!open ? "rotate-0 opacity-100" : "-rotate-90 opacity-0 absolute inset-2"}`}>
              <Menu size={24} />
            </span>
          </button>
        </div>
      </div>

      {/* Mobile Menu - only render when open */}
      {open && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/60 z-40 lg:hidden"
            onClick={() => setOpen(false)}
          />
          
          {/* Menu */}
          <nav
            className="lg:hidden bg-[#0a0a0e] border-t border-white/10 px-6 py-6 space-y-3 relative z-50"
            style={{ WebkitBackfaceVisibility: 'hidden' }}
          >
            {links.map((link, index) => {
              const isActive = activeSection === link.href;
              return (
                <a
                  key={`menu-${link.name}`}
                  href={link.href}
                  onClick={(e) => handleSmoothScroll(e, link.href)}
                  className={`block px-4 py-3 rounded-xl text-base font-medium ${
                    isActive
                      ? "text-purple-400 bg-purple-500/10 border border-purple-500/20"
                      : "text-gray-300 hover:text-white hover:bg-white/5"
                  }`}
                  style={{ WebkitBackfaceVisibility: 'hidden' }}
                >
                  {link.name}
                </a>
              );
            })}
            <div className="pt-4 space-y-3 border-t border-white/10">
              <a
                href="https://refundyoursol.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full px-5 py-3 rounded-xl bg-white/10 text-white font-medium border border-white/20 hover:bg-white/20 transition-colors"
                style={{ WebkitBackfaceVisibility: 'hidden' }}
              >
                Launch App
                <ExternalLink size={16} />
              </a>
              <a
                href="https://swap.pump.fun/?output=8gHPxqgHj6JQ2sQtMSghQYVN5qRP8wm5T6HNejuwpump"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center w-full px-5 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 transition-colors"
                style={{ WebkitBackfaceVisibility: 'hidden' }}
              >
                Buy $RYS
              </a>
            </div>
          </nav>
        </>
      )}
    </header>
  );
}
